<template>
  <div
    class="timeblock-default"
    @click="openTimeBlockShowDetailsModal"
    ref="calendarEntryRef"
    :title="getTooltipText"
    @mouseenter="mouseEnterHandler"
    @mouseleave="mouseLeaveHandler"
    :class="{ 'conflict-entry': isConflict, 'cancelled-entry': isCancelled }"
    :style="{
      backgroundColor: terminEntryDTO.veranstaltung
        ? terminEntryDTO.veranstaltung.color
        : '',
    }"
  >
    <div
      v-if="
        (terminEntryDTO &&
          calendarEntryIsHighEnough &&
          terminEntryDTO.bezeichnung) ||
        (isHovered && terminEntryDTO && showText)
      "
      class="timeblock-body"
    >
      <div class="timeblock-event">
        {{ terminEntryDTO.veranstaltung ? terminEntryDTO.veranstaltung.bezeichnung : terminEntryDTO.bezeichnung }}
      </div>
      <div
        v-if="terminEntryDTO.veranstaltung"
        class="timeblock-subject"
      >
        {{ terminEntryDTO.veranstaltung.kuerzel }}
      </div>
      <div class="timeblock-subject timeblock-time-row">
        <fa-icon
          icon="fa-solid fa-clock"
          class="mr-1"
        />
        {{ terminEntryDTO.von_uhrzeit }} - {{ terminEntryDTO.bis_uhrzeit }}
      </div>
      <div
        v-if="terminEntryDTO.dozenten.length > 0"
        class="timeblock-subject"
      >
        <span
          v-for="dozent in terminEntryDTO.dozenten"
          :key="dozent.id || dozent.person_name"
        >
          <fa-icon
            :icon="['fad', 'chalkboard-teacher']"
            class="mr-1"
          />
          {{ dozent.person_name }}
          <br />
        </span>
      </div>
      <div
        v-if="terminEntryDTO.raumliste.length > 0"
        class="timeblock-subject"
      >
        <span
          v-for="raum in terminEntryDTO.raumliste"
          :key="raum.id || raum.bezeichnung"
        >
          <fa-icon
            :icon="['fad', 'building']"
            class="mr-1"
          />
          {{ raum.bezeichnung }}
          <br />
        </span>
      </div>
      <div
        v-if="terminConflicts.length > 0"
        class="entry-conflicts"
      >
        <div class="conflicts-title">Conflicts:</div>
        <div
          v-for="(conflict, index) in terminConflicts"
          :key="index"
          class="conflict-item"
        >
          <span class="conflict-type">{{ conflict.type }}:</span>
          <span class="conflict-name">{{ conflict.designation }}</span>
          <span class="info-icon">
            <fa-icon
              :icon="['fas', 'fa-info-circle']"
              :title="'Termin: ' + conflict.tooltip"
            />
          </span>
        </div>
      </div>
    </div>
    <div
      v-else-if="calendarEntryIsHighEnough || (isHovered && showText)"
      class="timeblock-body"
    >
      (No subject)
      <div class="timeblock-subject timeblock-time-row">
        <fa-icon
          icon="fa-solid fa-clock"
          class="mr-1"
        />
        {{ terminEntryDTO.von_uhrzeit }} - {{ terminEntryDTO.bis_uhrzeit }}
      </div>
    </div>
    <div
      v-if="terminEntryDTO && terminEntryDTO.id"
      class="timeblock-info"
    >
      <font-awesome-icon
        class="timeblock-action"
        @click.stop="openTermin"
        :icon="['fas', 'up-right-from-square']"
        v-tooltip.hover.text="$t('global.toAppointment')"
      />
      <font-awesome-icon
        class="timeblock-action"
        :icon="['fad', 'pencil-alt']"
        @click.stop="openEditFromEntry"
        v-tooltip.hover.text="$t('global.edit')"
      />
    </div>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex'
  import { FontAwesomeIcon as FaIcon } from '@fortawesome/vue-fontawesome'

  export default {
    name: 'CalendarEntry',

    components: {
      FaIcon,
    },

    props: {
      terminEntryDTO: {
        type: Object,
        required: true,
      },
      dayConflicts: {
        type: Array,
        default: () => [],
      },
    },

    data() {
      return {
        calendarEntryRef: null,
        additionalInfoIsHighEnough: false,
        calendarEntryIsHighEnough: false,
        calendarEntryIsWideEnough: false,
        resizeObserver: null,
        isHovered: false,
        showText: false,
      }
    },

    computed: {
      ...mapState('VisualPlannerSelectionValue', ['projectWithIssuesList']),

      terminConflicts() {
        const conflicts = this.dayConflicts.find(
          (conflict) => conflict.termin_id === this.terminEntryDTO.id
        )

        if (!conflicts) return []

        return [
          ...conflicts.room_conflicts.flatMap((conflict) => [
            {
              type: 'Room',
              designation: conflict.conflicting_resource,
              tooltip: conflict.designation.join(','),
              conflictId: conflict.termin_id,
            },
          ]),
          ...conflicts.people_conflicts.flatMap((conflict) => [
            {
              type: 'People',
              designation: conflict.conflicting_resource,
              tooltip: conflict.designation.join(','),
              conflictId: conflict.termin_id,
            },
          ]),
        ]
      },

      isConflict() {
        return this.terminConflicts.length > 0
      },

      isCancelled() {
        return this.terminEntryDTO.ressourcenstatus?.id === '17506F1D261'
      },

      issue() {
        return this.projectWithIssuesList
          .find((project) => project.id === this.terminEntryDTO.projectId)
          ?.issues.find((issue) => issue.id === this.terminEntryDTO.issueId)
      },

      project() {
        return this.projectWithIssuesList.find(
          (project) => project.id === this.terminEntryDTO.projectId
        )
      },

      getTooltipText() {
        if (
          (!this.isHovered || !this.calendarEntryIsWideEnough) &&
          (!this.additionalInfoIsHighEnough || !this.calendarEntryIsWideEnough)
        ) {
          return this.getIssueSubject()
        }
        return ''
      },
    },

    methods: {
      ...mapActions('FeatureMetadata', ['openTimeBlockModal']),
      ...mapActions('VisualPlannerMetadata', ['setActiveTerminEntry']),
      ...mapActions('VisualPlannerMetadata', ['setActiveTerminConflict']),

      mouseEnterHandler() {
        if (
          !this.additionalInfoIsHighEnough &&
          !this.calendarEntryIsHighEnough
        ) {
          this.isHovered = true
        }

        setTimeout(() => {
          this.showText = true
        }, 100)
      },

      mouseLeaveHandler() {
        this.isHovered = false

        setTimeout(() => {
          this.showText = false
        }, 100)
      },

      getIssueSubject() {
        if (this.terminEntryDTO?.bezeichnung && !this.isHovered) {
          return `${
            this.terminEntryDTO.veranstaltung?.bezeichnung
              ? this.terminEntryDTO.veranstaltung?.bezeichnung
              : ''
          }\n${this.terminEntryDTO.bezeichnung}\n${
            this.terminEntryDTO.von_uhrzeit
          } - ${this.terminEntryDTO.bis_uhrzeit}`
        }
        return `(No subject)\n${this.terminEntryDTO.von_uhrzeit} - ${this.terminEntryDTO.bis_uhrzeit}`
      },

      openTimeBlockShowDetailsModal() {
        this.setActiveTerminEntry(this.terminEntryDTO)
        this.setActiveTerminConflict(this.terminConflicts)
        this.openTimeBlockModal()
      },

      initResizeObserver() {
        this.resizeObserver = new ResizeObserver(([entry]) => {
          const { height, width } = entry.contentRect

          if (
            !entry.target.classList.contains('calendar-entry-to-small') &&
            !this.isHovered
          ) {
            this.calendarEntryIsHighEnough = height > 30
            this.additionalInfoIsHighEnough = height > 75
            this.calendarEntryIsWideEnough = width > 100
          }
        })

        if (this.$refs.calendarEntryRef) {
          this.resizeObserver.observe(this.$refs.calendarEntryRef)
        }
      },

      updateCalendarEntryClass() {
        if (this.$refs.calendarEntryRef) {
          if (this.isHovered && !this.calendarEntryIsHighEnough) {
            this.$refs.calendarEntryRef.classList.add('calendar-entry-to-small')
          } else {
            this.$refs.calendarEntryRef.classList.remove(
              'calendar-entry-to-small'
            )
          }
        }
      },

      openTermin() {
        const routeData = this.$router.resolve({
          name: 'termin',
          params: { id: this.terminEntryDTO.id, anzeige: 0 },
        })
        window.open(routeData.href, '_blank')
      },
      openEditFromEntry() {
        this.$bus.$emit(
          'openNeuerTerminModal:edit',
          'edit-appointment-modal',
          this.terminEntryDTO
        )
        // optional: dem Parent sagen, dass er Popups schliessen soll
        this.$emit('hide-popup')
      },
    },

    mounted() {
      this.initResizeObserver()
    },

    beforeDestroy() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
      }
    },

    watch: {
      isHovered: {
        handler() {
          this.updateCalendarEntryClass()
        },
        immediate: true,
      },

      calendarEntryIsHighEnough: {
        handler() {
          this.updateCalendarEntryClass()
        },
      },
    },
  }
</script>

<style scoped lang="scss">
  @import '@/features/visual_planner/scss/color';

  .timeblock-default {
    width: 100%;
    position: absolute;
    background: $color-bg-secondary;
    color: $color-text-primary;
    border: 1px solid $color-border-secondary;
    border-left: 6px solid $color-border-secondary;
    border-radius: 4px;
    display: flex;
    flex-direction: row;
    align-items: start;
    transition: min-height 0.3s ease-in-out;
    min-height: 0;
    overflow: hidden;

    &.conflict-entry {
      border-color: $color-danger;
    }

    &.cancelled-entry {
      opacity: 0.5;
      text-decoration: line-through;
      border-left-color: $color-text-secondary;
    }
  }

  .timeblock-body {
    width: 100%;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: default;
    padding-left: 8px;
    padding-right: 36px;

    text-indent: 0;
  }

  .timeblock-body:first-child {
    padding-top: 4px;
  }

  .timeblock-event {
    font-size: 1rem;
    font-weight: bold;
  }

  .timeblock-subject {
    padding-top: 4px;
    font-size: 0.75rem;
  }

  .timeblock-time-row {
    display: flex;
    align-items: center;
    gap: 4px;
    opacity: 0.7;
  }

  .timeblock-info {
    position: absolute;
    top: 4px;
    right: 4px;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;

    background: transparent;
    padding: 0;
    z-index: 2;

    opacity: 0; // standard: unsichtbar
    pointer-events: none; // standard: nicht klickbar
    transition: opacity 0.12s ease-in-out;
  }

  /* nur bei Hover über dem ganzen Eintrag einblenden */
  .timeblock-default:hover .timeblock-info {
    opacity: 1;
    pointer-events: auto;
  }

  .timeblock-action {
    cursor: pointer;
    opacity: 0.85;
  }

  .timeblock-action:hover {
    opacity: 1;
  }

  .timeblock-default.calendar-entry-to-small:hover {
    min-height: 150px;
    width: auto;
    background: $color-primary-dark;
    z-index: 1000;
    border: 2px solid $color-accent;
  }

  // .timeblock-default.calendar-entry-to-small:hover .timeblock-info {
  //   border-color: $color-text-primary;
  // }

  .entry-conflicts {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid $color-border-primary;
    font-size: 0.75rem;

    .conflicts-title {
      color: $color-danger;
      font-weight: bold;
      margin-bottom: 4px;
    }

    .conflict-item {
      margin-bottom: 2px;
      display: flex;
      align-items: center;

      .conflict-type {
        color: $color-text-secondary;
        margin-right: 4px;
      }

      .conflict-name {
        color: $color-text-primary;
      }
    }
  }

  .info-icon {
    cursor: help;
    margin-left: auto;
    padding-left: 8px;
  }
</style>
